import { useCaptcha } from '@/hooks/useCaptcha'
import CaptchaProvider, { CaptchaNotice } from '@/providers/Captcha'
import Dialog from 'global/guides/Dialog'
import { useAuth } from 'hooks'
import { CircleCheckBig } from 'lucide-react'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
    avaliacao,
    AvaliacaoPendingResponse,
    CreateAvaliacaoInput,
    CreateAvaliacaoSchema,
} from 'service/api'
import { toast } from 'sonner'
import { FormAvaliacao } from './atoms/forms'

function isPendingResponse(data: unknown): data is AvaliacaoPendingResponse {
    return (
        typeof data === 'object' &&
        data !== null &&
        'pendingConfirmation' in data &&
        (data as AvaliacaoPendingResponse).pendingConfirmation === true
    )
}

function FormularioAvaliacaoContent() {
    const { handleLogin } = useAuth()
    const [pendingConfirmation, setPendingConfirmation] =
        useState<AvaliacaoPendingResponse | null>(null)
    const { execute: executeCaptcha } = useCaptcha({
        onError: err => toast.error(err),
    })
    const [params] = useSearchParams()

    const { mutateAsync, isLoading } = avaliacao.mutations.useCreate({
        onSuccess: async data => {
            if (isPendingResponse(data)) {
                setPendingConfirmation(data)
                return
            }

            toast.success(`Avaliação criada com sucesso!`)
            if ('Token' in data && data.Token) {
                handleLogin(data.Token)
            }
        },
        onError: err => {
            toast.error(err.message)
        },
    })

    async function handleSubmit(values: CreateAvaliacaoInput) {
        const token = await executeCaptcha('avaliacao')
        if (!token) {
            toast.error('Erro ao validar o captcha. Tente novamente.')
            return
        }

        values.captchaToken = token
        await mutateAsync(values)
    }

    if (pendingConfirmation) {
        return (
            <Dialog.Root width="38rem" height="auto">
                <Dialog.Title>Confirme seu email</Dialog.Title>
                <Dialog.Content dividers>
                    <div className="flex flex-col items-center gap-6 py-4">
                        <CircleCheckBig className="text-green-500 size-24" />
                        <div className="text-center space-y-2">
                            <p className="text-lg font-semibold">Quase lá!</p>
                            <p className="mt-4">
                                Enviamos um email de confirmação para{' '}
                                <span className="font-semibold">
                                    {pendingConfirmation.email}
                                </span>
                            </p>
                            <p>
                                Clique no link do email para ativar sua conta e
                                começar seu período de teste grátis de 14 dias.
                            </p>
                            <p className="text-sm mt-4">
                                Não esqueça de verificar sua caixa de spam!
                            </p>
                        </div>
                    </div>
                </Dialog.Content>
                <Dialog.Actions>
                    <Dialog.Actions.SecondaryButton
                        onClick={() => setPendingConfirmation(null)}
                    >
                        Voltar
                    </Dialog.Actions.SecondaryButton>
                </Dialog.Actions>
            </Dialog.Root>
        )
    }

    return (
        <Dialog.Root
            loading={isLoading}
            onSubmit={handleSubmit}
            schema={CreateAvaliacaoSchema}
            width="38rem"
            defaultValues={{
                emailResponsavel:
                    params.get('emailResponsavel') || params.get('email'),
                nomeResponsavel:
                    params.get('nomeResponsavel') || params.get('nome'),
                nomeFantasia:
                    params.get('nomeFantasia') || params.get('empresa'),
                telefone: params.get('telefone') || params.get('fone'),
            }}
        >
            <Dialog.Title>Cadastre-se e ganhe 14 dias grátis!</Dialog.Title>
            <Dialog.Content dividers>
                <FormAvaliacao />
                <CaptchaNotice className="mt-4" />
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton>
                    Cadastrar
                </Dialog.Actions.PrimaryButton>
            </Dialog.Actions>
        </Dialog.Root>
    )
}

export function FormularioAvaliacao() {
    return (
        <CaptchaProvider>
            <FormularioAvaliacaoContent />
        </CaptchaProvider>
    )
}
